/**
 * OCS parameter-shape registry.
 *
 * WHY THIS FILE EXISTS
 *   The OCS JSON-RPC surface is not uniform. Some methods take a params OBJECT
 *   (`{ "listPrepaidPackageTemplate": { "resellerId": 1 } }`) and some take a
 *   BARE INTEGER (`{ "listDetailedLocationZone": 1 }`). Sending the wrong one
 *   does not degrade gracefully: OCS rejects the entire request with
 *
 *     Cannot deserialize value of type java.lang.Integer from Object value
 *
 *   and the Console renders a dead panel with no data and no error.
 *
 *   This bug class has now shipped THREE times:
 *     1. `:zoneId` vs `:locationZoneId` (PR #659)
 *     2. `listDetailedLocationZone` receiving `{}` (network.ts, intelligence.ts)
 *     3. `listSponsor` receiving `{}` — same ternary, found only by auditing
 *        every sibling call site rather than fixing the one that was reported.
 *
 *   Prose comments at each call site did not stop (2) or (3). A single pinned
 *   list plus a test that reads it is the enforcement.
 *
 * SOURCES (both required to add an entry)
 *   - docs/research/bridge4ip-ocs-api/endpoints-full.md — the request example
 *     shows a bare number vs an object.
 *   - apps/mcp-server/src/tools.ts "Fix #12/#13/#14/#15" — these shapes were
 *     proven against the live OCS server, not inferred.
 *
 * NOTE ON packages/ocs-spec/ocs-methods.json
 *   Do NOT read param shape from that file. Its `params: {}` means "no named
 *   parameters documented", which is true for BOTH a bare-int method and a
 *   genuinely argument-less one. It cannot distinguish the two, so using it as
 *   the shape source is how a bare-int method gets `{}` again.
 */

/**
 * Methods whose sole parameter is a bare integer RESELLER id.
 * Callers must pass `resolveOcsResellerId(...)`'s number, never an object.
 */
export const OCS_BARE_INT_RESELLER_METHODS = [
  "listSponsor",
  "listSteeringList",
  "listDetailedDestinationList",
  "listDetailedLocationZone",
  "getCustomerTariff",
] as const;

/**
 * Methods whose sole parameter is a bare integer that is NOT a reseller id.
 * Kept separate so nobody "helpfully" feeds them a reseller id.
 */
export const OCS_BARE_INT_OTHER_METHODS = {
  listLocationZoneElement: "locationZoneId",
  listDestinationListPrefix: "destinationListId",
  listVoipTariffRule: "voipPlanId",
  getSimProviderStatus: "simId",
  deleteSubscriberPackage: "packageId",
} as const;

/**
 * Per-method FIELD-NAME translation, MCP/public name → OCS wire name.
 *
 * A sibling of the shape bug above, with the same failure mode: OCS rejects the
 * whole request rather than ignoring a field it does not know, so one wrong key
 * costs the entire call.
 *
 *   getResellerInfo({ resellerId: 1170 })
 *     → code 2, Unrecognized field "resellerId" ... (one known property: "id")
 *
 * WHY TRANSLATE RATHER THAN RENAME THE TOOL PARAMETER
 *   `resellerId` is the honest domain name and is the published contract of
 *   `@carrierllc/mcp` (npm, 0.9.2). OCS's `id` is a terse backend field whose
 *   meaning is only clear from the method it sits on. Renaming the tool
 *   parameter would break every existing caller to make the public surface
 *   worse, so the mapping lives here instead — at the one place every surface
 *   serialises through, which is also what makes it cover call sites nobody has
 *   written yet.
 *
 * SCOPE
 *   Keyed by method, never global: `id` means something different on every OCS
 *   method, so a repo-wide `resellerId → id` rewrite would corrupt the calls it
 *   did not intend to fix.
 *
 * VERIFIED against live OCS (ocs.esimvault.cloud), 2026-08-24:
 *   getResellerInfo {resellerId:1170} → code 2, unrecognized field
 *   getResellerInfo {id:1170}         → code 0, Lifecycle Innovations, parent Bridge4IP (246)
 *
 * modifyPPTCore / modifyPPTRecurring / modifyPPTThrottling — OCS names the
 * template id `prepaidpackagetemplateid` on all three modifyPPT* methods (see
 * packages/ocs-spec/ocs-accepted-params.json); the tools send `templateId`.
 * packageTemplateModifyParams emits the other OCS names and strips a body
 * prepaidpackagetemplateid so this rename of templateId is the one that lands.
 * Core still renames a leftover name, volume, or validityDays. An explicit
 * target wins.
 */
export const OCS_PARAM_RENAMES: Record<string, Record<string, string>> = {
  getResellerInfo: { resellerId: "id" },
  modifyPPTCore: {
    templateId: "prepaidpackagetemplateid",
    name: "prepaidpackagetemplatename",
    volume: "databyte",
    validityDays: "perioddays",
  },
  modifyPPTRecurring: { templateId: "prepaidpackagetemplateid" },
  modifyPPTThrottling: { templateId: "prepaidpackagetemplateid" },
  // Console NL-create and the MCP tool both advertise `name` / `volume` /
  // `validityDays`. OCS createPrepaidPackageTemplate rejects unknown keys
  // (Jackson code 2) — live failure was Unrecognized field "name".
  createPrepaidPackageTemplate: {
    name: "prepaidpackagetemplatename",
    volume: "databyte",
    validityDays: "perioddays",
    dataLimit: "databyte",
  },
  createLocationZone: { name: "locationZoneName" },
};

/**
 * Applies the registry to one request's params.
 *
 * Returns the input untouched when there is nothing to rename, so the common
 * path allocates nothing. An explicitly-supplied target key always wins: a
 * caller that already speaks OCS (tools-backlog.ts sends `id` directly) must
 * not have its correct value clobbered by a translated one.
 */
export function applyParamRenames<T>(method: string, params: T): T {
  const renames = OCS_PARAM_RENAMES[method];
  if (!renames || !params || typeof params !== "object" || Array.isArray(params)) {
    return params;
  }
  const record = params as Record<string, unknown>;
  let out: Record<string, unknown> | undefined;
  for (const [from, to] of Object.entries(renames)) {
    if (!Object.prototype.hasOwnProperty.call(record, from)) continue;
    out ??= { ...record };
    if (!Object.prototype.hasOwnProperty.call(record, to)) out[to] = record[from];
    delete out[from];
  }
  return (out ?? params) as T;
}

export type OcsBareIntResellerMethod =
  (typeof OCS_BARE_INT_RESELLER_METHODS)[number];

export function isBareIntResellerMethod(
  method: string,
): method is OcsBareIntResellerMethod {
  return (OCS_BARE_INT_RESELLER_METHODS as readonly string[]).includes(method);
}

/**
 * Every method whose sole parameter is a bare integer, regardless of what that
 * integer identifies.
 */
export function isBareIntMethod(method: string): boolean {
  return (
    isBareIntResellerMethod(method) ||
    Object.prototype.hasOwnProperty.call(OCS_BARE_INT_OTHER_METHODS, method)
  );
}

/** What the bare integer identifies, for a precise error message. */
export function bareIntIdName(method: string): string {
  if (isBareIntResellerMethod(method)) return "resellerId";
  return (
    (OCS_BARE_INT_OTHER_METHODS as Record<string, string>)[method] ?? "id"
  );
}

/**
 * Throws when a bare-int method is about to be sent an object.
 *
 * WHY THIS IS A RUNTIME CHECK AND NOT ONLY A TEST
 *   A pinned list that only tests read is documentation: it cannot stop a NEW
 *   call site written tomorrow, and it did not stop three of them. Three
 *   methods in this very registry (listLocationZoneElement,
 *   listDestinationListPrefix, deleteSubscriberPackage) were still being called
 *   with objects on 2026-08-20 and reproduced the Jackson error against live
 *   production OCS while the registry sat here, correct and unconsumed.
 *
 *   Enforcing at the single serialisation chokepoint covers every surface at
 *   once — API, MCP server, MCP stdio, CLI — including call sites nobody has
 *   written yet. That is the property a grep guard cannot have.
 *
 *   The thrown error names the expected id, so the failure is self-explaining
 *   rather than surfacing as an opaque OCS deserialiser message four layers
 *   away from the mistake.
 */
export function assertBareIntParam(method: string, params: unknown): void {
  if (!isBareIntMethod(method)) return;
  if (typeof params === "number") return;
  // A numeric string still serialises as a JSON string, which OCS also rejects.
  const shape =
    params === null
      ? "null"
      : Array.isArray(params)
        ? "array"
        : typeof params === "object"
          ? "object"
          : typeof params;
  throw new TypeError(
    `OCS method "${method}" takes a BARE INTEGER ${bareIntIdName(method)}, ` +
      `but received ${shape}. Sending an object makes OCS reject the whole ` +
      `request with "Cannot deserialize value of type java.lang.Integer from ` +
      `Object value", which surfaces in the UI as an empty panel. ` +
      `Pass the id itself, e.g. call("${method}", 1170).`,
  );
}

/**
 * modifyAccountBalance — Sam's decision (2026-08-28) on the last of the
 * seven param-drift KNOWN_BROKEN entries left semantic after the six-tool
 * fix on 2026-08-27.
 *
 * WHY `mode` NEVER REACHES THE WIRE
 *   The tool's public contract keeps `mode: 'adapt' | 'set'` — that part is
 *   not the defect. What's wrong is sending `mode` itself to OCS: `mode` is
 *   not a property this method knows about, so every call from the old
 *   handler was rejected outright before it could move a cent.
 *
 * THE MAPPING
 *   'adapt' sends a signed delta under `amount` (negative to subtract),
 *   never `setBalance`. 'set' sends the target value under `setBalance` and
 *   omits `amount` entirely. Matches Bridge4IP's own documented contract
 *   (docs/research/bridge4ip-ocs-api/endpoints-full.md §1.2) and the sibling
 *   `modifySubscriberBalance` method exactly — neither needs a direction
 *   classifier field.
 *
 * A `type: 'credit'|'debit'` FIELD WAS TRIED AND DROPPED
 *   A param-enumeration probe (packages/ocs-spec/ocs-accepted-params.json,
 *   2026-08-24) listed `type` as a known property on the OCS model, so an
 *   earlier version of this fix sent `type` alongside `amount`. That probe
 *   technique surfaces every field the backend's Java model knows about,
 *   not just the documented/supported API surface — it cannot distinguish a
 *   real input from a vestigial or internal one. Live-tested 2026-08-28
 *   directly against OCS (zero-amount, balance-0 account, so no state
 *   change either way): `type` absent, `type: "credit"`, `type: "debit"`,
 *   and `type: "some_bogus_value"` all produced an identical response. Not
 *   required, value doesn't matter — dropped in favour of the documented,
 *   sibling-consistent shape.
 */
